import React, { useState, useEffect } from 'react';
import api from '../utils/api';

const MemberReportHistoryPanel = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    fetchReportHistory();
  }, []);

  const fetchReportHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get reports for the last 30 days
      const response = await api.get('/reports/my-history', { params: { days: 30 } });

      setReports(response.data.data || []);
    } catch (error) {
      console.error('Error fetching report history:', error);
      setError(error.response?.data?.error || 'Failed to load report history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'submitted':
      case 'locked':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">✓ Submitted</span>;
      case 'draft':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">⚪ Draft</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 My Report History</h3>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 My Report History (Last 30 Days)</h3>

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {reports.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No reports found. Start submitting your daily reports!
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <div
              key={report.id}
              onClick={() => setSelectedReport(selectedReport?.id === report.id ? null : report)}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition cursor-pointer"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">{formatDate(report.report_date)}</p>
                  <p className="text-sm text-gray-600">
                    {report.submitted_at
                      ? `Submitted at ${new Date(report.submitted_at).toLocaleTimeString()}`
                      : 'Not submitted yet'}
                  </p>
                </div>
                {getStatusBadge(report.status)}
              </div>

              {/* Expanded Details */}
              {selectedReport?.id === report.id && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Yesterday:</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {report.yesterday_text || '(No content)'}
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Today:</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">
                      {report.today_text || '(No content)'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        onClick={fetchReportHistory}
        className="mt-4 w-full px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition text-sm font-medium"
      >
        🔄 Refresh History
      </button>
    </div>
  );
};

export default MemberReportHistoryPanel;
